import { Sparkles } from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/lib/i18n';
import { env } from '@/config/env';
import { hasInitData } from '@/lib/telegram/initData';

interface PlaceholderProps {
  pageName: string;
}

/**
 * Single shared placeholder used by every Wave A route. It surfaces
 * enough diagnostic context (env state, initData presence) that a
 * developer opening the app outside Telegram immediately knows whether
 * the dev fallback is wired.
 */
export function Placeholder({ pageName }: PlaceholderProps): JSX.Element {
  const t = useT();
  const initDataPresent = hasInitData();

  return (
    <AppShell>
      <PageHeader
        title={t('app.name')}
        description={t('app.tagline')}
        action={<Badge variant="gold">Wave A</Badge>}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent-gold" aria-hidden />
            {t('placeholder.title', { page: pageName })}
          </CardTitle>
          <CardDescription>{t('placeholder.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-3 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-text-muted">API base</dt>
              <dd className="font-mono text-xs text-text-primary">
                {env.VITE_API_BASE_URL}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-text-muted">TON network</dt>
              <dd>
                <Badge variant={env.VITE_TON_NETWORK === 'mainnet' ? 'gold' : 'muted'}>
                  {env.VITE_TON_NETWORK}
                </Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-text-muted">Telegram initData</dt>
              <dd>
                <Badge variant={initDataPresent ? 'success' : 'warning'}>
                  {initDataPresent ? 'present' : 'missing'}
                </Badge>
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </AppShell>
  );
}
